import axios from "axios";
import type { CustomDomain, CustomDomainResponse } from "@/types/customDomain";
import type { Branding } from "@/types/branding";

// Create Axios instance with default config
export const client = axios.create({
    baseURL: "/api", // Base is /api, we prepend /v1 or /gateway as needed
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true, // Important for cookies
});

// Response interceptor for error handling
client.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle 401 Unauthorized globally (optional)
        if (error.response?.status === 401) {
            // Redirect to upstream login or handled by component
            console.warn("Unauthorized, session might be invalid");
        }
        return Promise.reject(error);
    }
);

export async function getCustomDomain(workspaceId: string): Promise<CustomDomainResponse | null> {
    try {
        const res = await client.get<CustomDomainResponse>(`/v1/workspaces/${workspaceId}/custom-domain`);
        return res.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        throw error;
    }
}

export async function createCustomDomain(workspaceId: string, domain: string): Promise<CustomDomain> {
    const res = await client.post<CustomDomain>(`/v1/workspaces/${workspaceId}/custom-domain`, { domain });
    return res.data;
}

export async function deleteCustomDomain(workspaceId: string): Promise<void> {
    await client.delete(`/v1/workspaces/${workspaceId}/custom-domain`);
}

export async function verifyCustomDomain(workspaceId: string): Promise<{ status: string }> {
    const res = await client.post<{ status: string }>(`/v1/workspaces/${workspaceId}/custom-domain/verify`);
    return res.data;
}

export async function updateWorkspaceBranding(workspaceId: string, branding: Partial<Branding>): Promise<Branding> {
    const res = await client.put<Branding>(`/v1/workspaces/${workspaceId}/branding`, branding);
    return res.data;
}

export async function getWorkspaceBranding(workspaceId: string): Promise<Branding> {
    const res = await client.get<Branding>(`/v1/workspaces/${workspaceId}/branding`);
    return res.data;
}

export async function uploadBrandingAsset(
  workspaceId: string,
  file: File,
  type: 'logo' | 'favicon',
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await client.post<{ url: string }>(
    `/v1/workspaces/${workspaceId}/branding/upload?type=${type}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}
